<script>
const MAX_PAGES_SHOWN = 5;

export default {
  name: 'ListPagination',
  props: {
    currentPage: { type: Number, default: 1 },
    perPage: { type: Number, default: 10 },
    total: { type: Number, default: 0 },
    totalPages: { type: Number, default: 0 },
    /** Plural noun for the count line, e.g. "utilisateurs". */
    noun: { type: String, default: 'éléments' },
  },
  emits: ['page-change'],
  computed: {
    countLabel() {
      if (!this.total) {
        return `0 ${this.noun}`;
      }

      const start = (this.currentPage - 1) * this.perPage + 1;
      const end = Math.min(this.currentPage * this.perPage, this.total);

      return `${start}–${end} sur ${this.total} ${this.noun}`;
    },

    pages() {
      const out = [];
      let start = Math.max(
        1,
        this.currentPage - Math.floor(MAX_PAGES_SHOWN / 2),
      );
      const end = Math.min(this.totalPages, start + MAX_PAGES_SHOWN - 1);

      if (end - start < MAX_PAGES_SHOWN - 1) {
        start = Math.max(1, end - MAX_PAGES_SHOWN + 1);
      }

      for (let i = start; i <= end; i += 1) {
        out.push(i);
      }

      return out;
    },
  },
  methods: {
    go(page) {
      if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
        this.$emit('page-change', page);
      }
    },
  },
};
</script>

<template>
  <div class="list-card__footer">
    <div class="list-pagination__count">{{ countLabel }}</div>

    <div v-if="totalPages > 1" class="list-pagination">
      <button
        type="button"
        class="list-pagination__step"
        :disabled="currentPage <= 1"
        aria-label="Page précédente"
        @click="go(currentPage - 1)"
      >
        <i class="ri-arrow-left-s-line"></i>
      </button>

      <button
        v-for="page in pages"
        :key="page"
        type="button"
        class="list-pagination__page"
        :class="{ 'list-pagination__page--active': page === currentPage }"
        :aria-current="page === currentPage ? 'page' : null"
        @click="go(page)"
      >
        {{ page }}
      </button>

      <button
        type="button"
        class="list-pagination__step"
        :disabled="currentPage >= totalPages"
        aria-label="Page suivante"
        @click="go(currentPage + 1)"
      >
        <i class="ri-arrow-right-s-line"></i>
      </button>
    </div>
  </div>
</template>

<style scoped>
.list-pagination__count {
  font-size: 13px;
  color: #6c757d;
}

.list-pagination {
  display: flex;
  gap: 4px;
  align-items: center;
}

.list-pagination__step,
.list-pagination__page {
  height: 30px;
  border: 1px solid #d1d5db;
  background: #fff;
  color: #6c757d;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  cursor: pointer;
}

.list-pagination__step {
  width: 30px;
}

.list-pagination__page {
  min-width: 30px;
  padding: 0 8px;
}

.list-pagination__step:hover:not(:disabled),
.list-pagination__page:hover {
  border-color: #878a99;
}

.list-pagination__step:disabled {
  opacity: 0.45;
  pointer-events: none;
}

.list-pagination__page--active {
  background: #005bd0;
  border-color: #005bd0;
  color: #fff;
}
</style>
